import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDialog from '../../components/UserDialog/ConfirmDialog';
import axios from 'axios';

import { AgGridReact } from 'ag-grid-react';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  RichSelectModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

// Register AG Grid Enterprise modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  RichSelectModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
]);

export default function SparrowUsersList() {
  const gridRef = useRef(null);

  // 1) State for “Assign Agents” dropdown:
  const [sparrowAgents, setSparrowAgents] = useState(
    []
  ); // only “agent” perms
  const [loadingAgents, setLoadingAgents] = useState(true);

  // 2) State for “Edit Agent Permissions” list (all agent perms):
  const [allAgentPerms, setAllAgentPerms] = useState([]);
  const [loadingAllPerms, setLoadingAllPerms] = useState(true);
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [newAgentSuffix, setNewAgentSuffix] = useState('');
  const [addingPerm, setAddingPerm] = useState(false);

  // For inline‐edit:
  const [editingPermId, setEditingPermId] = useState(null);
  const [editingPermName, setEditingPermName] = useState('');

  // 3) “ConfirmDialog” state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  // Distinguish two contexts:
  //   pendingGlobalPerm → { id, name } for deleting across all users
  //   pendingUserPerm   → { id, name, userId } for deleting for one user
  const [pendingGlobalPerm, setPendingGlobalPerm] = useState(null);
  const [pendingUserPerm, setPendingUserPerm] = useState(null);

  // 4) State for user‐grid:
  const [rowData, setRowData] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // 5) “Edit Assigned Agents” dialog:
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: '',
    name: '',
    userName: '',
    userGroupId: '',
    userGroupName: '',
    assignedSparrowAgents: [], // array of { id, name }
  });
  const [agentToAdd, setAgentToAdd] = useState(null);

  // ── UTILITY: fetch only perms containing “agent” for dropdown ──
  const fetchAgentPermissions = async () => {
    setLoadingAgents(true);
    try {
      const { data } = await axios.get('/api/Admin/user-permissions-basic');
      const filtered = data
        .filter((p) => p.name.toLowerCase().includes('agent'))
        .map((p) => ({ id: p.permissionId, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSparrowAgents(filtered);
    } catch (err) {
      console.error('Failed to fetch agent permissions', err);
      setSparrowAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  // ── UTILITY: fetch ALL perms containing “agent” for global editing ──
  const fetchAllAgentPerms = async () => {
    setLoadingAllPerms(true);
    try {
      const { data } = await axios.get('/api/Admin/user-permissions-basic');
      const filtered = data
        .filter((p) => p.name.toLowerCase().includes('agent'))
        .map((p) => ({ id: p.permissionId, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAllAgentPerms(filtered);
    } catch (err) {
      console.error('Failed to fetch all agent perms', err);
      setAllAgentPerms([]);
    } finally {
      setLoadingAllPerms(false);
    }
  };

  // ── UTILITY: fetch only users whose group has Sparrow access ──
  const fetchNestedUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(
        '/api/Admin/users-assigned-nested-permissions'
      );
      const usersWithAgents = data.filter(
        (u) => u.userGroupHasSparrowAccess === true
      );
      const rows = usersWithAgents.map((u) => ({
        id: u.id,
        name: u.name,
        userName: u.userName,
        userGroupId: u.userGroupId ?? '',
        userGroupName: u.userGroupName ?? '',
        assignedSparrowAgents: (u.assignedSparrowAgents || []).map((s) => ({
          id: s.permissionId,
          name: s.name,
        })),
      }));
      setRowData(rows);
    } catch (err) {
      console.error(
        'Failed to fetch users with assigned agent permissions',
        err
      );
      setRowData([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // ── ON MOUNT: load dropdown  grid ──
  useEffect(() => {
    fetchAgentPermissions();
    fetchNestedUsers();
  }, []);

  // ── AG Grid column definitions ──
  const columnDefs = useMemo(
    () => [
      { headerName: 'ID', field: 'id', hide: true },
      {
        headerName: 'Edit',
        field: 'actions',
        minWidth: 100,
        cellRenderer: (params) => (
          <IconButton
            size="small"
            onClick={() => handleEditClick(params.data)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      { headerName: 'Name', field: 'name', flex: 1, sort: 'asc' },
      { headerName: 'Username', field: 'userName', flex: 1 },
      { headerName: 'GroupId', field: 'userGroupId', hide: true },
      { headerName: 'Group', field: 'userGroupName', flex: 1 },
      {
        headerName: 'Assigned Agents',
        field: 'assignedSparrowAgents',
        flex: 2,
        cellStyle: { textAlign: 'left', whiteSpace: 'normal' },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (params) => {
          const list = params.value || [];
          return list.map((p) => p.name).join(', ');
        },
        cellRenderer: (params) => {
          const list = params.value || [];
          if (list.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#888' }}
              >
                None
              </Typography>
            );
          }
          return (
            <Box>
              {list.map((s) => (
                <Typography key={s.id} variant="body2">
                  {s.name}
                </Typography>
              ))}
            </Box>
          );
        },
      },
    ],
    []
  );

  // ── “Edit” clicked in grid: open “Assign Agents” dialog ──
  const handleEditClick = (row) => {
    setIsEditMode(true);
    setCurrentUser({
      id: row.id,
      name: row.name,
      userName: row.userName,
      userGroupId: row.userGroupId,
      userGroupName: row.userGroupName,
      assignedSparrowAgents: row.assignedSparrowAgents.slice(),
    });
    setAgentToAdd(null);
    setDialogOpen(true);
  };

  // ── Add an agent to this user (local) ──
  const handleAddAgentClick = () => {
    if (!agentToAdd) return;
    const exists = currentUser.assignedSparrowAgents.some(
      (p) => p.id === agentToAdd.id
    );
    if (!exists) {
      setCurrentUser((prev) => ({
        ...prev,
        assignedSparrowAgents: [
          ...prev.assignedSparrowAgents,
          agentToAdd,
        ],
      }));
    }
    setAgentToAdd(null);
  };

  // ── Prompt confirm to remove this perm “for this user” ──
  const promptConfirmRemoveForUser = (agent) => {
    setPendingUserPerm({ id: agent.id, name: agent.name, userId: currentUser.id });
    setConfirmMessage(
      `You are about to remove “${agent.name}” from ${currentUser.name}.`
    );
    setConfirmOpen(true);
  };

  // ── Remove a permission just for this user ──
  const deleteForThisUser = async () => {
    if (!pendingUserPerm) {
      setConfirmOpen(false);
      return;
    }
    const { userId, id: permId } = pendingUserPerm;
    try {
      await axios.delete(`/api/Admin/permission/${userId}/${permId}`);
      // Refresh this dialog’s list  grid:
      setCurrentUser((prev) => ({
        ...prev,
        assignedSparrowAgents: prev.assignedSparrowAgents.filter(
          (p) => p.id !== permId
        ),
      }));
      await fetchNestedUsers();
    } catch (err) {
      console.error(
        `Failed to remove permission ${permId} for user ${userId}`,
        err
      );
    } finally {
      setPendingUserPerm(null);
      setConfirmOpen(false);
    }
  };

  // ── Save assigned‐agents list back to server ──
  const handleSaveAssignments = async () => {
    const payload = {
      userId: currentUser.id,
      PermissionIds: currentUser.assignedSparrowAgents.map((p) => p.id),
    };
    try {
      await axios.put('/api/Admin/user/assigned-agents', payload);
      await fetchNestedUsers();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save assigned agents', err);
    }
  };

  // ── OPEN “Edit Agent Permissions” dialog ──
  const openEditAgentPermDialog = async () => {
    setNewAgentSuffix('');
    await fetchAllAgentPerms();
    setPermDialogOpen(true);
  };

  // ── Save new “SPARROW_AGENT_ACCESS_<suffix>” perm (global) ──
  const handleAddAgentPermission = async () => {
    const suffix = newAgentSuffix.trim().toUpperCase();
    if (!suffix) return;
    const fullName = `SPARROW_AGENT_ACCESS_${suffix}`;
    setAddingPerm(true);
    try {
      await axios.post(
        `/api/Admin/add-permission?name=${encodeURIComponent(fullName)}`
      );
      setNewAgentSuffix('');
      await fetchAllAgentPerms();
      await fetchAgentPermissions();
    } catch (err) {
      console.error('Failed to add agent permission', err);
    } finally {
      setAddingPerm(false);
    }
  };

  // ── Prepare to delete a “global” permission (all users) ──
  const promptConfirmDeleteGlobal = (perm) => {
    setPendingGlobalPerm({ id: perm.id, name: perm.name });
    setConfirmMessage(`You are about to delete “${perm.name}” from all users.`);
    setConfirmOpen(true);
  };

  // ── Delete a permission across all users ──
  const deleteForAllUsers = async () => {
    if (!pendingGlobalPerm) {
      setConfirmOpen(false);
      return;
    }
    const { id: permId } = pendingGlobalPerm;
    try {
      await axios.delete(`/api/Admin/permission/${permId}`);
      await fetchAllAgentPerms();
      await fetchAgentPermissions();
      await fetchNestedUsers();
    } catch (err) {
      console.error(`Failed to delete permission ${permId}`, err);
    } finally {
      setPendingGlobalPerm(null);
      setConfirmOpen(false);
    }
  };

  // ── Cancel any pending confirm ──
  const cancelConfirm = () => {
    setPendingGlobalPerm(null);
    setPendingUserPerm(null);
    setConfirmOpen(false);
  };

  // ── Begin inline‐rename of a global permission ──
  const beginEditPerm = (perm) => {
    setEditingPermId(perm.id);
    setEditingPermName(perm.name);
  };

  // ── Cancel inline‐rename ──
  const cancelEditPerm = () => {
    setEditingPermId(null);
    setEditingPermName('');
  };

  // ── Save inline‐renamed global permission ──
  const handleSaveEditedPerm = async () => {
    const permId = editingPermId;
    const newName = editingPermName.trim();
    if (!permId || !newName) return;
    try {
      await axios.put(
        `/api/Admin/permission/${permId}?name=${encodeURIComponent(newName)}`
      );
      await fetchAllAgentPerms();
      await fetchAgentPermissions();
    } catch (err) {
      console.error(`Failed to update permission ${permId}`, err);
    } finally {
      setEditingPermId(null);
      setEditingPermName('');
    }
  };

  return (
    <Box>
      {/* ── HEADER: “Edit Agent Permissions” ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          onClick={openEditAgentPermDialog}
        >
          Edit Agent Permissions
        </Button>
      </Box>

      {/* ── AG Grid for users ── */}
      <div style={{ height: '74vh', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
            suppressRowClickSelection: true,
          }}
          animateRows
          loading={loadingUsers}
          defaultColDef={{ resizable: true }}
          domLayout="autoHeight"
        />
      </div>

      {/* ── EDIT ASSIGNED AGENTS DIALOG ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? `Edit Agents for ${currentUser.name}` : 'Assign Agents'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* 1) Autocomplete dropdown */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                sx={{ flexGrow: 1 }}
                options={sparrowAgents}
                getOptionLabel={(opt) => opt.name}
                value={agentToAdd}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                loading={loadingAgents}
                onChange={(e, newVal) => setAgentToAdd(newVal)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pick an agent to add"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAgents ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleAddAgentClick}
                disabled={!agentToAdd}
              >
                Add
              </Button>
            </Box>

            {/* 2) List of currently‐assigned agents */}
            <List dense>
              {currentUser.assignedSparrowAgents.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', color: '#888', p: 1 }}
                >
                  No agents assigned.
                </Typography>
              )}
              {currentUser.assignedSparrowAgents.map((agent) => (
                <ListItem key={agent.id} sx={{ py: 0, px: 1 }}>
                  <ListItemText primary={agent.name} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => promptConfirmRemoveForUser(agent)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAssignments}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT AGENT PERMISSIONS DIALOG ── */}
      <Dialog
        open={permDialogOpen}
        onClose={() => setPermDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Agent Permissions</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 1) Create new agent permission by suffix */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Suffix (e.g. JOHN_DOE)"
                value={newAgentSuffix}
                onChange={(e) => setNewAgentSuffix(e.target.value)}
                placeholder="Enter suffix only"
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleAddAgentPermission}
                disabled={!newAgentSuffix.trim() || addingPerm}
              >
                {addingPerm ? 'Adding…' : 'Add'}
              </Button>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                The final permission name will be&nbsp;
                <strong>
                  SPARROW_AGENT_ACCESS_{newAgentSuffix.trim().toUpperCase() || '...'}
                </strong>
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />

            {/* 2) Scrollable list of allAgentPerms with inline‐edit  delete */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                mt: 1,
              }}
            >
              {loadingAllPerms ? (
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                  Loading…
                </Typography>
              ) : allAgentPerms.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', color: '#888', p: 1 }}
                >
                  No agent permissions found.
                </Typography>
              ) : (
                <List dense>
                  {allAgentPerms.map((perm) => (
                    <ListItem key={perm.id} sx={{ py: 0, px: 1 }}>
                      {editingPermId === perm.id ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <TextField
                            value={editingPermName}
                            onChange={(e) => setEditingPermName(e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            onClick={handleSaveEditedPerm}
                            sx={{ ml: 1 }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditPerm}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <ListItemText primary={perm.name} />
                          <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => beginEditPerm(perm)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => promptConfirmDeleteGlobal(perm)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── ConfirmDialog used by both flows ── */}
      <ConfirmDialog
        open={confirmOpen}
        message={confirmMessage}
        onConfirm={() => {
          if (pendingGlobalPerm) {
            deleteForAllUsers();
          } else if (pendingUserPerm) {
            deleteForThisUser();
          }
        }}
        /// Ensure that clicking “Cancel” (or outside) calls cancelConfirm:
        onClose={cancelConfirm}
        onCancelClick={cancelConfirm}
        showCancel={true}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Box>
  );
}
